import type { CSSProperties } from 'react';

export const BLUR_FILTER = 'blur(8px)';

/** Not split into halves: one fully blurred layer the finger wipes clear (see useMagicReveal) */
export const MAGIC_REVEAL = 'magic_reveal';

/**
 * Radial's circle: `closest-side` sizes the gradient to half the element's shorter side, so a
 * stop at 50% gives a true circle whose radius is 25% of the shorter side on any aspect ratio.
 * clip-path's circle() can't express that (its percentages resolve against the diagonal), so
 * both halves are masks.
 */
const RADIAL_CIRCLE = 'circle closest-side at 50% 50%';

/**
 * The circle's edge feathers from clear to blurred between these two stops (centered on the 50%
 * radius). The two halves' masks are exact complements, so across the band the sharp and blurred
 * halves cross-fade. A wider band softens the edge but shows a little more detail near it.
 */
const RADIAL_FEATHER_START = '45%';
const RADIAL_FEATHER_END = '55%';

const clipPath = (value: string): CSSProperties => ({ clipPath: value, WebkitClipPath: value });

/**
 * Magic Reveal's layer, clipped to its own box: otherwise the blur bleeds past the photo's edges
 * as a soft shadow until the ink's mask (which also clips) is first drawn.
 */
export const MAGIC_REVEAL_SHAPE = clipPath('inset(0)');

/** A ring (radial's outer half) can't be one clip-path polygon/ellipse, so it uses a mask */
const maskImage = (value: string): CSSProperties => ({ maskImage: value, WebkitMaskImage: value });

/**
 * The two complementary shapes for each blur style. The first half starts clear and the second
 * starts blurred; a long press swaps which one carries the blur, without changing the shapes.
 */
export const BLUR_PATTERNS: Record<
    Exclude<SpApi.ScreenshotDeterrenceMobileStyle, typeof MAGIC_REVEAL>,
    [CSSProperties, CSSProperties]
> = {
    diagonal: [
        clipPath('polygon(0 0, 100% 0, 0 100%)'), // top-left triangle
        clipPath('polygon(100% 0, 100% 100%, 0 100%)') // bottom-right triangle
    ],
    horizontal: [
        clipPath('polygon(0 0, 100% 0, 100% 50%, 0 50%)'), // top half
        clipPath('polygon(0 50%, 100% 50%, 100% 100%, 0 100%)') // bottom half
    ],
    radial: [
        maskImage(
            `radial-gradient(${RADIAL_CIRCLE}, #000 ${RADIAL_FEATHER_START}, transparent ${RADIAL_FEATHER_END})`
        ), // center
        maskImage(
            `radial-gradient(${RADIAL_CIRCLE}, transparent ${RADIAL_FEATHER_START}, #000 ${RADIAL_FEATHER_END})`
        ) // outer ring
    ]
};

/** Whether the given half (0 or 1) is the blurred one */
export const isHalfBlurred = (half: 0 | 1, isSwapped: boolean) => (half === 1) !== isSwapped;
